import React, { useState, useEffect } from 'react';
import {
  Search, Filter, X, ChevronDown, ChevronUp,
  Gem, CheckCircle, XCircle, MapPin, Store, User,
  Eye, Edit, Play, Clock, Package, History, AlertCircle, Save
} from 'lucide-react';
import { API_URL } from '../../services/api';
import FicheBijouTermineModal from '../Atelier/FicheBijouTermineModal';
import FicheCalculMoteur from './FicheCalculMoteur';
import { isVendeuse } from '../../utils/typeCaisseLabels';

const API = API_URL;
const SERVER = API_URL.replace('/api', '');
const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

// Normalise une image_url vers une URL absolue affichable
const buildImgUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Normalise les backslash Windows en forward slash
  const clean = url.replace(/\\/g, '/');
  // URL de l'API style /api/bijoux-images/view/...
  if (clean.startsWith('/api/')) return `${SERVER}${clean}`;
  // URL relative /uploads/... ou /static/...
  return `${SERVER}${clean}`;
};

const TYPE_LABELS = {
  BO: 'Boucles d\'oreilles', BAGUE: 'Bague', BRACELET: 'Bracelet',
  PENDENTIF: 'Pendentif', COLLIER: 'Collier', GOURMETTE: 'Gourmette'
};

const COULEUR_BADGE = {
  BLANC: 'bg-gray-100 text-gray-700 border border-gray-300',
  ROSE:  'bg-pink-100 text-pink-700 border border-pink-300',
  JAUNE: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
};

const fmtDH = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} DH`;

// Section dépliable de la fiche pièce — toutes au même niveau, sobres
const SectionFiche = ({ id, titre, icone, ouverte, onToggle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
      <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
        <span>{icone}</span>{titre}
      </span>
      {ouverte ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </button>
    {ouverte && <div className="px-4 py-4 space-y-3">{children}</div>}
  </div>
);

export default function CatalogueBijoux({ onOuvrirFicheFab, onLancerProduction, onVoirPieces }) {
  const [modeles, setModeles]       = useState([]);
  const [filtres, setFiltres]       = useState({ collection: '', type: '', search: '' });
  const [collections, setCollections] = useState([]);
  const [types, setTypes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showFiltres, setShowFiltres] = useState(true);
  // §113 - Modèles avec stock uniquement (param ignoré tant que le backend §114 n'est pas déployé)
  const [stockOnly, setStockOnly]   = useState(true);
  // Variation or
  const [showVariationOr, setShowVariationOr] = useState(false);
  const [variationPct, setVariationPct] = useState('');
  const [variationScope, setVariationScope] = useState('all');
  // Fiche pièce
  const [fichePiece, setFichePiece]     = useState(null);  // données complètes
  const [loadingFiche, setLoadingFiche] = useState(false);
  // §113d - Sub-modal fiche OF complète (étapes, devis vs réel)
  const [showFicheOF, setShowFicheOF]   = useState(false);
  // §205 - vendeuse (rôle 'boutique') : pas d'accès fiche OF (coût/marge/étapes)
  const vendeuse = (() => {
    try { return isVendeuse(JSON.parse(localStorage.getItem('user') || '{}')?.role || ''); }
    catch { return false; }
  })();
  // Edition inline de reference_article
  const [editingRef, setEditingRef]     = useState(null);  // { id, reference_article }
  const [savingRef, setSavingRef]       = useState(false);
  // Indicateur d'écart prix/cours (10/06/2026) — indicateur seul, réétiquetage MANUEL
  const [ecarts, setEcarts]             = useState({});    // bijou_id -> {statut, ecart, pvp_actuel, prix_propose}
  const [ecartsCompteurs, setEcartsCompteurs] = useState(null);
  const [filtreSousEvalues, setFiltreSousEvalues] = useState(false);
  const [reetiquetage, setReetiquetage] = useState(null);  // { piece, prix } en cours de confirmation
  // Fiche de calcul (détail moteur PR/PVP) — sections accordéon, chargées au 1er dépliage
  const [sectionsOuvertes, setSectionsOuvertes] = useState({ infos: true });
  const [calc, setCalc]                 = useState(null);   // prix-calcule au cours de fabrication
  const [calcJour, setCalcJour]         = useState(null);   // prix-calcule au cours du jour
  const [calcLoading, setCalcLoading]   = useState(false);

  const toggleSection = (id) => {
    setSectionsOuvertes(s => ({ ...s, [id]: !s[id] }));
    // lazy-load du détail moteur dès qu'une section coût est dépliée
    if (!sectionsOuvertes[id] && ['calcul', 'etapes', 'packaging', 'ecart'].includes(id)
        && !calc && !calcLoading && fichePiece?.id && !vendeuse) {
      chargerCalc(fichePiece.id);
    }
  };

  const chargerCalc = async (bijouId) => {
    setCalcLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/bijoux/${bijouId}/prix-calcule`, { headers: headers() }),
        fetch(`${API}/bijoux/${bijouId}/prix-calcule?cours=jour`, { headers: headers() }),
      ]);
      if (r1.ok) setCalc(await r1.json());
      if (r2.ok) setCalcJour(await r2.json());
    } catch (e) { /* fiche de calcul non bloquante */ }
    finally { setCalcLoading(false); }
  };

  useEffect(() => { chargerFiltres(); if (!vendeuse) chargerEcarts(); }, []); // eslint-disable-line

  const chargerEcarts = async () => {
    try {
      const res = await fetch(`${API}/bijoux/ecarts-valorisation`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        const map = {};
        (d.items || []).forEach(i => { map[i.bijou_id] = i; });
        setEcarts(map);
        setEcartsCompteurs(d.compteurs || null);
      }
    } catch (e) { /* indicateur non bloquant */ }
  };

  const appliquerPrix = async () => {
    if (!reetiquetage) return;
    const { piece, prix, motif } = reetiquetage;
    try {
      const res = await fetch(`${API}/bijoux/${piece.id}/prix`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ nouveau_prix: parseFloat(prix), motif: motif || 'reetiquetage_cours' }),
      });
      const d = await res.json();
      if (!res.ok) { alert(d.error || 'Erreur'); return; }
      setReetiquetage(null);
      // si la fiche est ouverte sur cette piece, rafraichir son prix affiche
      setFichePiece(f => (f && f.id === piece.id)
        ? { ...f, prix_vente: d.prix_vente, pvp_magasin: d.pvp_magasin } : f);
      await Promise.all([chargerModeles(), chargerEcarts()]);
    } catch (e) { alert('Erreur réseau'); }
  };

  const ECART_BADGE = {
    sous_evalue:  { dot: 'bg-red-500',    label: 'Sous-évalué' },
    a_surveiller: { dot: 'bg-orange-400', label: 'À surveiller' },
    ok:           { dot: 'bg-green-500',  label: 'OK' },
  };
  const PastilleEcart = ({ pieceId }) => {
    const e = ecarts[pieceId];
    if (!e || !ECART_BADGE[e.statut]) return null;
    const cfg = ECART_BADGE[e.statut];
    const tip = e.ecart != null
      ? `${cfg.label} — valeur au cours actuel : ${Number(e.pvp_actuel).toLocaleString('fr-FR')} DH (écart ${e.ecart >= 0 ? '+' : ''}${Number(e.ecart).toLocaleString('fr-FR')} DH)`
      : cfg.label;
    return <span title={tip} className={`inline-block w-2.5 h-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />;
  };
  useEffect(() => { chargerModeles(); }, [filtres, stockOnly]); // eslint-disable-line

  const chargerFiltres = async () => {
    try {
      const res = await fetch(`${API}/bijoux/catalogue/collections`, { headers: headers() });
      if (res.ok) { const d = await res.json(); setCollections(d.collections || []); setTypes(d.types || []); }
    } catch (e) { console.error('Erreur chargement filtres:', e); }
  };

  const chargerModeles = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filtres.collection) p.append('collection', filtres.collection);
    if (filtres.type)       p.append('type', filtres.type);
    if (filtres.search)     p.append('search', filtres.search);
    if (stockOnly)          p.append('stock_only', 'true');
    try {
      const res = await fetch(`${API}/bijoux/catalogue/modeles?${p}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setModeles(Array.isArray(data) ? data : []);
      } else {
        console.error('Erreur API catalogue:', res.status);
        setModeles([]);
      }
    } catch (e) {
      console.error('Erreur réseau catalogue:', e);
      setModeles([]);
    } finally { setLoading(false); }
  };

  // Jeu AFFICHE = modeles serveur-filtres + filtre client "sous-evalues".
  // L'entete s'appuie dessus -> compteurs et valeur suivent TOUS les filtres.
  const modelesAffiches = filtreSousEvalues
    ? modeles.filter(m => (m.pieces || []).some(p => p.inventaire_actif && ecarts[p.id]?.statut === 'sous_evalue'))
    : modeles;
  const totalEnStock = modelesAffiches.reduce((t, m) => t + (parseInt(m.nb_en_stock) || 0), 0);
  const totalVendus  = modelesAffiches.reduce((t, m) => t + (parseInt(m.nb_vendus) || 0), 0);
  const valeurStockPvp = modelesAffiches.reduce((t, m) =>
    t + (m.pieces || []).filter(pc => pc.inventaire_actif)
        .reduce((tt, pc) => tt + parseFloat(pc.pvp_magasin || pc.prix_vente || 0), 0), 0);

  const ouvrirFiche = async (piece) => {
    setLoadingFiche(true);
    setSectionsOuvertes({ infos: true });
    setCalc(null); setCalcJour(null);
    setFichePiece({ ...piece, _loading: true });
    try {
      const res = await fetch(`${API}/bijoux/${piece.id}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        // Charger aussi l'OF associé
        let of_data = null;
        if (data.ordre_fabrication_id) {
          const res2 = await fetch(`${API}/atelier/ordres/${data.ordre_fabrication_id}`, { headers: headers() });
          if (res2.ok) of_data = await res2.json();
        }
        setFichePiece({ ...data, of_data, _loading: false });
      }
    } catch (e) { console.error(e); }
    finally { setLoadingFiche(false); }
  };

  const sauvegarderRef = async () => {
    if (!editingRef) return;
    setSavingRef(true);
    try {
      const res = await fetch(`${API}/bijoux/${editingRef.id}/personnaliser`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify({ reference_article: editingRef.reference_article.trim().toUpperCase() }),
      });
      if (res.ok) {
        chargerModeles();
        setEditingRef(null);
      }
    } catch (e) { console.error(e); }
    finally { setSavingRef(false); }
  };

  return (
    <div className="space-y-4 pb-8">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Gem className="h-8 w-8 text-yellow-600" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Catalogue Stock</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-11">
            {modelesAffiches.length} modele{modelesAffiches.length > 1 ? 's' : ''} · {totalEnStock} piece{totalEnStock > 1 ? 's' : ''} en stock · {totalVendus} vendu{totalVendus > 1 ? 's' : ''} · Valeur stock (PVP) : <span className="font-bold text-gray-700">{valeurStockPvp.toLocaleString('fr-FR')} DH</span>
          </p>
        </div>
        <button onClick={() => setShowVariationOr(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100 text-sm font-semibold transition-colors">
          <span>Variation Or</span>
        </button>
      </div>

      {/* Modal variation or */}
      {showVariationOr && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Revalorisation prix catalogue</h3>
            <p className="text-sm text-gray-500 mb-4">Appliquer une variation en % sur les prix de vente. Positif = hausse, negatif = baisse.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pourcentage de variation</label>
                <div className="flex items-center gap-2">
                  <input type="text" value={variationPct} onChange={e => setVariationPct(e.target.value.replace(',', '.'))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-lg font-bold text-center" placeholder="Ex: 5 ou -3" />
                  <span className="text-xl font-bold text-gray-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Appliquer a</label>
                <select value={variationScope} onChange={e => setVariationScope(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="all">Tout le catalogue</option>
                  <option value="bague">Bagues uniquement</option>
                  <option value="collier">Colliers uniquement</option>
                  <option value="bracelet">Bracelets uniquement</option>
                  <option value="boucles_oreilles">Boucles d'oreilles</option>
                  <option value="pendentif">Pendentifs</option>
                </select>
              </div>
              {variationPct && (
                <div className={`p-3 rounded-lg text-sm font-semibold ${parseFloat(variationPct) > 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                  {parseFloat(variationPct) > 0 ? 'Hausse' : 'Baisse'} de {Math.abs(parseFloat(variationPct))}% sur {variationScope === 'all' ? 'tout le catalogue' : variationScope}
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowVariationOr(false); setVariationPct(''); }}
                className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium">Annuler</button>
              <button onClick={async () => {
                if (!variationPct || parseFloat(variationPct) === 0) return;
                if (!window.confirm(`Appliquer ${variationPct}% sur ${variationScope === 'all' ? 'tout le catalogue' : variationScope} ?`)) return;
                try {
                  const res = await fetch(`${API}/bijoux/catalogue/revalorisation`, {
                    method: 'POST', headers: headers(),
                    body: JSON.stringify({ pourcentage: parseFloat(variationPct), categorie: variationScope === 'all' ? null : variationScope })
                  });
                  const d = await res.json();
                  if (res.ok) {
                    alert(`${d.nb_modifies || 0} bijou(x) mis a jour`);
                    setShowVariationOr(false); setVariationPct('');
                    chargerModeles();
                  } else {
                    alert('Erreur: ' + (d.error || 'inconnue'));
                  }
                } catch (e) { alert('Erreur: ' + e.message); }
              }}
                disabled={!variationPct || parseFloat(variationPct) === 0}
                className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:bg-gray-300">
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par référence ou désignation..."
              value={filtres.search}
              onChange={e => setFiltres({ ...filtres, search: e.target.value })}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltres(!showFiltres)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm transition-colors ${showFiltres ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </button>
          {(filtres.collection || filtres.type || filtres.search) && (
            <button
              onClick={() => setFiltres({ collection: '', type: '', search: '' })}
              className="flex items-center space-x-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm transition-colors"
            >
              <X className="h-4 w-4" />
              <span>Réinitialiser</span>
            </button>
          )}
          {!vendeuse && ecartsCompteurs && (ecartsCompteurs.sous_evalue || 0) > 0 && (
            <button
              onClick={() => setFiltreSousEvalues(!filtreSousEvalues)}
              title="Bijoux dont le prix étiquette est nettement inférieur à leur valeur au cours actuel de l'or"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                filtreSousEvalues ? 'bg-red-600 text-white border-red-600' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
              }`}
            >
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              Sous-évalués ({ecartsCompteurs.sous_evalue})
            </button>
          )}
        </div>

        {showFiltres && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Collection</label>
              <select
                value={filtres.collection}
                onChange={e => setFiltres({ ...filtres, collection: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400"
              >
                <option value="">Toutes les collections</option>
                {collections.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type de bijou</label>
              <select
                value={filtres.type}
                onChange={e => setFiltres({ ...filtres, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400"
              >
                <option value="">Tous les types</option>
                {types.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
              </select>
            </div>
            <div className="flex items-end col-span-2 sm:col-span-1">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={stockOnly}
                  onChange={e => setStockOnly(e.target.checked)}
                  className="h-4 w-4 text-yellow-500 rounded border-gray-300 focus:ring-yellow-400"
                />
                Stock uniquement
                <span className="text-xs text-gray-400">(décocher pour relancer un modèle épuisé)</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Liste des modèles */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin h-10 w-10 border-4 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : modeles.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Gem className="h-12 w-12 mx-auto mb-3" />
          <p>Aucun modèle trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {modelesAffiches.map(modele => {
            const expanded = expandedId === modele.id;
            const nbStock  = modele.nb_en_stock || 0;
            const nbVendus = modele.nb_vendus || 0;
            return (
              <div key={modele.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Ligne modèle cliquable */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(expanded ? null : modele.id)}
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    {/* Miniature bijou */}
                    <div className="flex-shrink-0">
                      {buildImgUrl(modele.image_url) ? (
                        <img
                          src={buildImgUrl(modele.image_url)}
                          alt={modele.reference_article}
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200 shadow-sm"
                          onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                        />
                      ) : null}
                      <div className={`w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center ${buildImgUrl(modele.image_url) ? 'hidden' : 'flex'}`}>
                        <Gem className="h-5 w-5 text-gray-300" />
                      </div>
                    </div>
                    {/* Ref article — badge avec deux actions */}
                    <div className="flex-shrink-0 flex flex-col gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); if (onOuvrirFicheFab && modele.id) onOuvrirFicheFab(modele.id); }}
                        className="inline-block bg-yellow-50 hover:bg-yellow-100 text-yellow-800 text-xs font-bold px-2.5 py-1 rounded-md border border-yellow-200 hover:border-yellow-400 font-mono cursor-pointer transition-colors"
                        title="Ouvrir la fiche fabrication"
                      >
                        {modele.reference_article}
                      </button>
                      {onVoirPieces && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onVoirPieces(modele.reference_article); }}
                          className="inline-block bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded border border-blue-200 hover:border-blue-400 cursor-pointer transition-colors whitespace-nowrap"
                          title="Voir les pièces individuelles"
                        >
                          📦 Pièces
                        </button>
                      )}
                    </div>

                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{modele.nom_modele}</p>
                      <div className="flex items-center space-x-2 mt-0.5 flex-wrap gap-y-1">
                        <span className="text-xs text-gray-500">{TYPE_LABELS[modele.type_bijou] || modele.type_bijou}</span>
                        {modele.collection_nom && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs font-medium text-indigo-600">{modele.collection_nom}</span>
                          </>
                        )}
                        {modele.couleur_or && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${COULEUR_BADGE[modele.couleur_or] || 'bg-gray-100 text-gray-600'}`}>
                            Or {modele.couleur_or.toLowerCase()}
                          </span>
                        )}
                        {modele.pvp_magasin && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs font-semibold text-green-700">{Number(modele.pvp_magasin).toLocaleString('fr-FR')} DH</span>
                          </>
                        )}
                        {modele.boutiques_stock && nbStock > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-1 text-xs text-blue-600">
                              <Store className="h-3 w-3 flex-shrink-0" />
                              {modele.boutiques_stock}
                            </span>
                          </>
                        )}
                        {modele.clients_acheteurs && nbVendus > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-1 text-xs text-purple-600">
                              <User className="h-3 w-3 flex-shrink-0" />
                              {modele.clients_acheteurs}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions + Compteurs + chevron */}
                  <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
                    {/* Boutons d'action */}
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); const piece = (modele.pieces || []).find(p => p.inventaire_actif); if (piece) ouvrirFiche(piece); }}
                        className="p-1.5 rounded hover:bg-blue-100 text-blue-500 transition-colors" title="Voir la fiche">
                        <Eye className="h-4 w-4" />
                      </button>
                      {onOuvrirFicheFab && (
                        <button onClick={(e) => { e.stopPropagation(); onOuvrirFicheFab(modele.id); }}
                          className="p-1.5 rounded hover:bg-purple-100 text-purple-500 transition-colors" title="Modifier fiche fabrication">
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {onLancerProduction && (
                        <button onClick={(e) => { e.stopPropagation(); onLancerProduction(modele); }}
                          className="p-1.5 rounded hover:bg-green-100 text-green-600 transition-colors" title="Lancer une production">
                          <Play className="h-4 w-4 fill-current" />
                        </button>
                      )}
                    </div>
                    <div className="h-6 w-px bg-gray-200" />
                    <div className="flex items-center space-x-3">
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{nbStock}</p>
                        <p className="text-xs text-gray-400">en stock</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-400">{nbVendus}</p>
                        <p className="text-xs text-gray-400">vendu{nbVendus > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    {expanded
                      ? <ChevronUp className="h-5 w-5 text-gray-400" />
                      : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>

                {/* Détail des pièces */}
                {expanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Pièces individuelles ({(modele.pieces || []).length})
                    </p>
                    <div className="space-y-2">
                      {(modele.pieces || []).length === 0 ? (
                        <p className="text-sm text-gray-400 italic">Aucune pièce enregistrée</p>
                      ) : (
                        (modele.pieces || []).map(piece => (
                          <div
                            key={piece.id}
                            className={`flex items-center justify-between rounded-lg px-4 py-2.5 border transition-colors ${
                              piece.inventaire_actif
                                ? 'bg-white border-green-100 hover:border-green-300'
                                : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {piece.inventaire_actif
                                ? <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                : <XCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              {/* Miniature pièce */}
                              {buildImgUrl(piece.piece_image_url || modele.image_url) && (
                                <img
                                  src={buildImgUrl(piece.piece_image_url || modele.image_url)}
                                  alt=""
                                  className="w-8 h-8 object-cover rounded border border-gray-200 flex-shrink-0"
                                  onError={e => { e.target.style.display='none'; }}
                                />
                              )}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Référence modèle */}
                                  <span className="font-mono font-bold text-sm text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                                    {piece.reference_article || modele.reference_article}
                                  </span>
                                  {/* ID pièce */}
                                  <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    {piece.id_produit}
                                  </span>
                                  {/* Couleur */}
                                  {piece.couleur_or && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                      piece.couleur_or === 'BLANC' ? 'bg-gray-100 text-gray-600' :
                                      piece.couleur_or === 'ROSE'  ? 'bg-pink-100 text-pink-600' :
                                                                      'bg-yellow-100 text-yellow-700'
                                    }`}>Or {piece.couleur_or.toLowerCase()}</span>
                                  )}
                                  {/* Localisation */}
                                  {piece.inventaire_actif && (piece.boutique_nom || piece.boutique_id) ? (
                                    <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                      <MapPin className="h-3 w-3" />{piece.boutique_nom || `Boutique ${piece.boutique_id}`}
                                    </span>
                                  ) : piece.inventaire_actif && !piece.boutique_id ? (
                                    <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                      <MapPin className="h-3 w-3" />Stock central
                                    </span>
                                  ) : null}
                                  {piece.type_creation === 'CLIENT' && piece.client_nom && (
                                    <span className="text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                                      👤 {piece.client_nom}
                                    </span>
                                  )}
                                </div>
                                {/* Statut vente */}
                                {!piece.inventaire_actif && piece.date_vente && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    Vendu le {new Date(piece.date_vente).toLocaleDateString('fr-FR')}
                                    {piece.client_acheteur ? ` · ${piece.client_acheteur}` : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              {!vendeuse && piece.inventaire_actif && <PastilleEcart pieceId={piece.id} />}
                              {piece.pvp_magasin && (
                                <span className="font-semibold text-gray-700 text-sm">
                                  {Number(piece.pvp_magasin).toLocaleString('fr-FR')} DH
                                </span>
                              )}
                              {/* Réétiquetage manuel au cours actuel (jamais auto) */}
                              {!vendeuse && piece.inventaire_actif && ecarts[piece.id]?.statut === 'sous_evalue' && (
                                <button
                                  onClick={() => setReetiquetage({ piece, prix: ecarts[piece.id].prix_propose })}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold transition-colors"
                                  title={`Valeur au cours actuel : ${Number(ecarts[piece.id].pvp_actuel).toLocaleString('fr-FR')} DH (+${Number(ecarts[piece.id].ecart).toLocaleString('fr-FR')} DH)`}
                                >
                                  Réétiqueter
                                </button>
                              )}
                              {/* Bouton fiche complète */}
                              <button
                                onClick={() => ouvrirFiche(piece)}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-medium transition-colors"
                                title="Voir la fiche complète"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                Fiche
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirmation réétiquetage au cours actuel (action manuelle) ── */}
      {reetiquetage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setReetiquetage(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-800 mb-1">
              {reetiquetage.motif === 'edition_manuelle' ? 'Modifier le prix de vente' : 'Réétiqueter au cours actuel'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {reetiquetage.piece.id_produit} · {reetiquetage.piece.nom || ''}
            </p>
            <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-1 mb-4">
              <div className="flex justify-between"><span className="text-gray-500">Prix étiquette actuel</span>
                <span className="font-semibold">{Number(ecarts[reetiquetage.piece.id]?.prix_vente ?? (reetiquetage.piece.pvp_magasin || reetiquetage.piece.prix_vente) ?? 0).toLocaleString('fr-FR')} DH</span></div>
              {ecarts[reetiquetage.piece.id]?.pvp_actuel != null && (
                <div className="flex justify-between"><span className="text-gray-500">Valeur au cours actuel</span>
                  <span className="font-semibold text-red-700">{Number(ecarts[reetiquetage.piece.id].pvp_actuel).toLocaleString('fr-FR')} DH</span></div>
              )}
              {ecarts[reetiquetage.piece.id]?.ecart != null && (
                <div className="flex justify-between"><span className="text-gray-500">Écart</span>
                  <span className="font-bold text-red-700">{ecarts[reetiquetage.piece.id].ecart >= 0 ? '+' : ''}{Number(ecarts[reetiquetage.piece.id].ecart).toLocaleString('fr-FR')} DH</span></div>
              )}
            </div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Nouveau prix</label>
            <input type="number" step="100" value={reetiquetage.prix}
                   onChange={e => setReetiquetage(r => ({ ...r, prix: e.target.value }))}
                   className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setReetiquetage(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Annuler</button>
              <button onClick={appliquerPrix} disabled={!parseFloat(reetiquetage.prix)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-40">
                Appliquer ce prix
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fiche complète d'une pièce ─────────────────────────────────── */}
      {fichePiece && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4" onClick={() => setFichePiece(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-700 to-blue-700 rounded-t-2xl text-white">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-xl">{fichePiece.reference_article}</span>
                  <span className="font-mono text-sm bg-white/20 px-2 py-0.5 rounded">{fichePiece.id_produit}</span>
                  {fichePiece.couleur_or && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Or {fichePiece.couleur_or?.toLowerCase()}</span>
                  )}
                </div>
                <p className="text-sm text-white/80 mt-0.5">{fichePiece.nom || fichePiece.description || '—'}</p>
              </div>
              <button onClick={() => setFichePiece(null)} className="p-2 hover:bg-white/20 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            {fichePiece._loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-3">

                {/* ── Section 1 : Infos générales (seule section visible vendeuse §205) ── */}
                <SectionFiche id="infos" titre="Infos générales" icone="ℹ️"
                  ouverte={!!sectionsOuvertes.infos} onToggle={toggleSection}>

                {/* Modifier la référence modèle */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">Référence modèle</p>
                  {editingRef?.id === fichePiece.id ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingRef.reference_article}
                        onChange={e => setEditingRef({ ...editingRef, reference_article: e.target.value.toUpperCase() })}
                        className="flex-1 px-3 py-1.5 border border-purple-300 rounded-lg font-mono font-bold text-purple-700 text-sm focus:ring-2 focus:ring-purple-400 uppercase"
                        autoFocus
                      />
                      <button onClick={sauvegarderRef} disabled={savingRef}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold">
                        {savingRef ? '...' : 'Sauver'}
                      </button>
                      <button onClick={() => setEditingRef(null)} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm">
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-lg text-purple-800">{fichePiece.reference_article || '—'}</span>
                      <button
                        onClick={() => setEditingRef({ id: fichePiece.id, reference_article: fichePiece.reference_article || '' })}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 underline"
                      >
                        <Edit className="h-3 w-3" /> Modifier
                      </button>
                    </div>
                  )}
                </div>

                {/* Infos générales */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Catégorie',   val: fichePiece.categorie },
                    { label: 'Couleur or',  val: fichePiece.couleur_or },
                    { label: 'Poids métal', val: fichePiece.poids_metal_net ? `${fichePiece.poids_metal_net} g` : fichePiece.poids ? `${fichePiece.poids} g` : null },
                    { label: 'Collection',  val: fichePiece.collection_nom },
                    { label: 'Certificat',  val: fichePiece.certificat },
                    { label: 'Date création', val: fichePiece.date_fabrication ? new Date(fichePiece.date_fabrication).toLocaleDateString('fr-FR') : fichePiece.created_at ? new Date(fichePiece.created_at).toLocaleDateString('fr-FR') : null },
                    { label: 'Statut',      val: fichePiece.statut_fabrication },
                  ].filter(r => r.val).map(({ label, val }) => (
                    <div key={label} className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-sm font-medium text-gray-800 capitalize">{val}</p>
                    </div>
                  ))}
                  {/* Prix vente — éditable (central/admin), maj prix_vente + pvp_magasin ensemble */}
                  {(fichePiece.prix_vente || fichePiece.pvp_magasin) && (
                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-500">Prix vente</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800">
                          {Number(fichePiece.pvp_magasin || fichePiece.prix_vente).toLocaleString('fr-FR')} DH
                        </p>
                        {!vendeuse && fichePiece.inventaire_actif !== 0 && (
                          <button
                            onClick={() => setReetiquetage({ piece: fichePiece, prix: fichePiece.pvp_magasin || fichePiece.prix_vente, motif: 'edition_manuelle' })}
                            className="text-blue-600 hover:text-blue-800" title="Modifier le prix">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description / Notes */}
                {(fichePiece.description || fichePiece.notes_variation || fichePiece.observation_piece) && (
                  <div className="space-y-2">
                    {fichePiece.description && fichePiece.description !== fichePiece.reference_article && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-500 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{fichePiece.description}</p>
                      </div>
                    )}
                    {fichePiece.notes_variation && (
                      <div className="bg-blue-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-blue-600 mb-1">Variations / personnalisations</p>
                        <p className="text-sm text-blue-800">{fichePiece.notes_variation}</p>
                      </div>
                    )}
                    {fichePiece.observation_piece && (
                      <div className="bg-yellow-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-yellow-600 mb-1">Observations atelier</p>
                        <p className="text-sm text-yellow-800">{fichePiece.observation_piece}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Localisation / Vente */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" /> Localisation &amp; parcours
                  </p>
                  <div className="space-y-2 text-sm">
                    {fichePiece.inventaire_actif ? (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        <span>En stock</span>
                        {fichePiece.boutique_nom && <span className="font-medium">· {fichePiece.boutique_nom}</span>}
                        {!fichePiece.boutique_id && <span className="text-gray-500">· Stock central</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-gray-600">
                        <XCircle className="h-4 w-4" />
                        <span>Hors stock</span>
                        {fichePiece.date_vente && (
                          <span>· Vendu le {new Date(fichePiece.date_vente).toLocaleDateString('fr-FR')}</span>
                        )}
                      </div>
                    )}
                    {fichePiece.client_acheteur && (
                      <div className="flex items-center gap-2 text-purple-700">
                        <User className="h-4 w-4" />
                        <span>Acheté par <strong>{fichePiece.client_acheteur}</strong></span>
                      </div>
                    )}
                    {fichePiece.num_bon && <p className="text-gray-500">Bon : {fichePiece.num_bon}</p>}
                    {fichePiece.num_facture && <p className="text-gray-500">Facture : {fichePiece.num_facture}</p>}
                  </div>
                </div>
                </SectionFiche>

                {/* ── Sections coûts/production — §205 : jamais rendues pour la vendeuse ── */}
                {!vendeuse && (
                  <>
                    {/* ── Section 2 : Fiche de calcul (détail moteur PR/PVP — composant partagé) ── */}
                    <SectionFiche id="calcul" titre="Fiche de calcul" icone="💰"
                      ouverte={!!sectionsOuvertes.calcul} onToggle={toggleSection}>
                      <FicheCalculMoteur
                        bijouId={fichePiece.id}
                        prixFige={fichePiece.pvp_magasin || fichePiece.prix_vente}
                        calc={calc}
                        loading={calcLoading}
                      />
                    </SectionFiche>

                    {/* ── Section 3 : Étapes & production ── */}
                    <SectionFiche id="etapes" titre="Étapes & production" icone="🔧"
                      ouverte={!!sectionsOuvertes.etapes} onToggle={toggleSection}>
                      {fichePiece.of_data ? (
                        <div className="grid grid-cols-2 gap-3 text-sm bg-blue-50 border border-blue-100 rounded-lg p-3">
                          <div>
                            <p className="text-xs text-gray-500">Numéro OF</p>
                            <p className="font-mono font-bold text-blue-800">{fichePiece.of_data.numero_ordre}</p>
                          </div>
                          {fichePiece.of_data.statut && (
                            <div>
                              <p className="text-xs text-gray-500">Statut</p>
                              <p className="font-medium capitalize">{fichePiece.of_data.statut}</p>
                            </div>
                          )}
                          {fichePiece.of_data.date_commande && (
                            <div>
                              <p className="text-xs text-gray-500">Date lancement</p>
                              <p className="font-medium">{new Date(fichePiece.of_data.date_commande).toLocaleDateString('fr-FR')}</p>
                            </div>
                          )}
                          {fichePiece.of_data.date_livraison_reelle && (
                            <div>
                              <p className="text-xs text-gray-500">Date livraison</p>
                              <p className="font-medium">{new Date(fichePiece.of_data.date_livraison_reelle).toLocaleDateString('fr-FR')}</p>
                            </div>
                          )}
                          {fichePiece.of_data.notes && (
                            <div className="col-span-2">
                              <p className="text-xs text-gray-500">Notes</p>
                              <p className="text-sm text-gray-700">{fichePiece.of_data.notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Pas d'ordre de fabrication lié (dépôt-vente, achat externe…).</p>
                      )}
                      {calcLoading && <p className="text-sm text-gray-400">Chargement des étapes…</p>}
                      {calc && Array.isArray(calc.facon_detail) && calc.facon_detail.length > 0 && (
                        <div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-gray-500 border-b border-gray-200">
                                <th className="text-left py-1.5 font-medium">Ouvrier</th>
                                <th className="text-right py-1.5 font-medium">Temps</th>
                                <th className="text-right py-1.5 font-medium">Coût/min</th>
                                <th className="text-right py-1.5 font-medium">Coût</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {calc.facon_detail.map((e, i) => (
                                <tr key={i}>
                                  <td className="py-1.5 font-medium text-gray-800">{e.ouvrier}</td>
                                  <td className="py-1.5 text-right text-gray-600">{Math.round(e.minutes)} min</td>
                                  <td className="py-1.5 text-right text-gray-600">{Number(e.cout_minute).toFixed(2)}</td>
                                  <td className="py-1.5 text-right font-medium">{fmtDH(e.montant)}</td>
                                </tr>
                              ))}
                              <tr className="font-bold text-gray-900">
                                <td className="py-2" colSpan={3}>Total façon</td>
                                <td className="py-2 text-right">{fmtDH(calc.pr?.facon)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                      {calc && (!calc.facon_detail || calc.facon_detail.length === 0) && (
                        <p className="text-sm text-gray-400">Aucune étape avec temps/ouvrier enregistrée.</p>
                      )}
                    </SectionFiche>

                    {/* ── Section 4 : Packaging / écrin ── */}
                    <SectionFiche id="packaging" titre="Packaging / écrin" icone="📦"
                      ouverte={!!sectionsOuvertes.packaging} onToggle={toggleSection}>
                      <div className="flex gap-2 flex-wrap">
                        {fichePiece.rhodiage      && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Rhodiage</span>}
                        {fichePiece.gravure_beldi && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Gravure beldi</span>}
                        {fichePiece.ecrin         && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Écrin</span>}
                        {fichePiece.garantie      && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Garantie</span>}
                        {fichePiece.fonte2        && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Fonte 2</span>}
                        {!fichePiece.rhodiage && !fichePiece.gravure_beldi && !fichePiece.ecrin && !fichePiece.garantie && !fichePiece.fonte2 && (
                          <span className="text-sm text-gray-400">Aucune option de fabrication cochée.</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {fichePiece.type_packaging && (
                          <div className="bg-gray-50 rounded-lg px-3 py-2">
                            <p className="text-xs text-gray-500">Type packaging</p>
                            <p className="font-medium text-gray-800">{fichePiece.type_packaging}</p>
                          </div>
                        )}
                        {calc && calc.pr?.ecrin ? (
                          <div className="bg-gray-50 rounded-lg px-3 py-2">
                            <p className="text-xs text-gray-500">Coût écrin (majoration)</p>
                            <p className="font-medium text-gray-800">{fmtDH(calc.pr.ecrin)}</p>
                          </div>
                        ) : null}
                      </div>
                    </SectionFiche>

                    {/* ── Section 5 : Écart prix figé vs cours actuel ── */}
                    <SectionFiche id="ecart" titre="Écart prix figé vs cours actuel" icone="⚠️"
                      ouverte={!!sectionsOuvertes.ecart} onToggle={toggleSection}>
                      {calcLoading && <p className="text-sm text-gray-400">Calcul en cours…</p>}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-500">Prix figé (étiquette)</p>
                          <p className="font-bold text-gray-800">{fmtDH(fichePiece.pvp_magasin || fichePiece.prix_vente)}</p>
                        </div>
                        {ecarts[fichePiece.id]?.pvp_actuel != null && (
                          <div className="bg-gray-50 rounded-lg px-3 py-2">
                            <p className="text-xs text-gray-500">PVP au cours actuel</p>
                            <p className="font-bold text-gray-800">{fmtDH(ecarts[fichePiece.id].pvp_actuel)}</p>
                          </div>
                        )}
                        {calcJour?.cours_or && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <p className="text-xs text-amber-700">Cours de l'or actuel</p>
                            <p className="font-bold text-amber-800">{calcJour.cours_or} DH/g</p>
                          </div>
                        )}
                        {ecarts[fichePiece.id]?.statut === 'sous_evalue' && (
                          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            <p className="text-xs text-red-600">Écart</p>
                            <p className="font-bold text-red-700">
                              sous-évalué {ecarts[fichePiece.id].ecart != null ? `(${Number(ecarts[fichePiece.id].ecart).toFixed(1)} %)` : ''}
                            </p>
                          </div>
                        )}
                      </div>
                      {ecarts[fichePiece.id]?.statut !== 'sous_evalue' && !calcLoading && (
                        <p className="text-sm text-green-700">✓ Prix aligné sur le cours actuel (écart sous le seuil).</p>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {ecarts[fichePiece.id]?.statut === 'sous_evalue' && ecarts[fichePiece.id]?.prix_propose != null && fichePiece.inventaire_actif !== 0 && (
                          <button
                            onClick={() => setReetiquetage({ piece: fichePiece, prix: ecarts[fichePiece.id].prix_propose, motif: 'reetiquetage_cours' })}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold">
                            Réétiqueter à {fmtDH(ecarts[fichePiece.id].prix_propose)}
                          </button>
                        )}
                        <button
                          onClick={() => { setFichePiece(null); setShowVariationOr(true); }}
                          className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 rounded-lg text-xs font-semibold">
                          Variation Or (réévaluation globale)
                        </button>
                      </div>
                    </SectionFiche>
                  </>
                )}

              </div>
            )}

            <div className="px-6 py-3 border-t border-gray-100 flex justify-between">
              <div className="flex gap-2">
                {onOuvrirFicheFab && (
                  <button onClick={() => { setFichePiece(null); onOuvrirFicheFab(fichePiece.id); }}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors">
                    <Edit className="h-4 w-4" />
                    Modifier fiche fabrication
                  </button>
                )}
                {/* §113d - Fiche OF complète (étapes, devis vs réel) — §205 : pas pour la vendeuse */}
                {!vendeuse && fichePiece.ordre_fabrication_id && !fichePiece._loading && (
                  <button onClick={() => setShowFicheOF(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                    title="Voir la fiche OF complète avec étapes">
                    <Package className="h-4 w-4" />
                    Voir l'OF {fichePiece.of_data?.numero_ordre || ''}
                  </button>
                )}
              </div>
              <button onClick={() => setFichePiece(null)}
                className="px-5 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-semibold">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* §113d - Sub-modal fiche OF complète (z-[60], au-dessus de la fiche pièce) — §205 : jamais pour la vendeuse */}
      {showFicheOF && fichePiece && !vendeuse && (
        <FicheBijouTermineModal
          bijou={fichePiece}
          onClose={() => setShowFicheOF(false)}
          onRelancerSuccess={() => { setShowFicheOF(false); setFichePiece(null); chargerModeles(); }}
        />
      )}
    </div>
  );
}
